import { BaseDialog, Button, PlanCard } from '@app/components';
import { motion } from 'framer-motion';
import { ContactUsEmail, planNames } from '@app/utils/consts';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { PlanInterface } from '@app/models/plans';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getPlans, updatePlan } from '@app/services/plans';
import { fetchMe } from '@app/redux/features/auth';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { PlanUsageTable } from '@app/components/PlanUsageTable/PlanUsageTable';
import { usePermissions } from '@app/contexts/PermissionContext';
import CloseIcon from '@app/assets/icons/close.svg?react';
import BriefcaseIcon from '@app/assets/icons/briefcase.svg?react';
import CheckIcon from '@app/assets/icons/check.svg?react';
import BulbIcon from '@app/assets/icons/menu/bulb.svg?react';

// Helper function to convert plan type to display name
const getPlanDisplayName = (planType: string): string => {
  switch (planType) {
    case 'BASIC':
      return 'Starter Plan';
    case 'PROFESSIONAL':
      return 'Professional Plan';
    case 'ENTERPRISE':
      return 'Enterprise Plan';
    default:
      return (
        planType.charAt(0).toUpperCase() +
        planType.slice(1).toLowerCase() +
        ' Plan'
      );
  }
};

// Helper function to get the correct icon for a plan type
const getPlanIcon = (planType: string) => {
  const planInfo = planNames.find((plan) => plan.name === planType);
  return planInfo?.Icon || BriefcaseIcon;
};

export const SettingsPlan = () => {
  const [selectedPlan, setSelectedPlan] = useState<PlanInterface | undefined>();
  const [lowerModal, setLowerModal] = useState(false);
  const [upperModal, setUpperModal] = useState(false);
  const { can } = usePermissions();
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);
  const business = useAppSelector((state) => state.business);
  const businessSubscription = auth?.user?.subscriptions?.find(
    (sub) => sub.businessId === business?.selected?.id
  );

  const handleSelectedPlan = (plan?: PlanInterface) => {
    if (!plan || !businessSubscription) {
      setSelectedPlan(undefined);
      setLowerModal(false);
      setUpperModal(false);
      return;
    }

    setSelectedPlan(plan);
    if (businessSubscription && plan) {
      if (businessSubscription?.plan.price > plan.price) {
        setLowerModal(true);
      }
      if (businessSubscription?.plan.price < plan.price) {
        setUpperModal(true);
      }
    }
  };

  const { data: plansResponse, isLoading } = useQuery({
    queryKey: ['get-plans'],
    queryFn: () => getPlans(),
  });

  const { mutate: updateSubscription, isPending } = useMutation({
    mutationKey: ['update-subscriptions'],
    mutationFn: (priceId: string) => updatePlan(priceId),
    onSuccess: () => {
      dispatch(fetchMe());
      handleSelectedPlan();
      toast.success('Subscription updated');
    },
    onError: (error: any) => {
      toast.error(
        error?.data?.message ||
          error?.response?.data?.message ||
          'Error updating subscriptions'
      );
      console.error(error);
    },
  });

  useEffect(() => {
    if (auth.user && !auth.user.subscriptions) {
      dispatch(fetchMe());
    }
  }, []);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="container mx-auto p-4"
      >
        {/* 1. Your current plan */}
        <div className="grid grid-cols-1 md:grid-cols-2 mb-8">
          <PlanCard businessSubscription={businessSubscription} />
        </div>

        {/* 3. Plan cards + Usage table */}
        {isLoading && (
          <div className="p-4 text-center">Loading plans and features...</div>
        )}
        {!isLoading && plansResponse && (
          <PlanUsageTable
            businessSubscription={businessSubscription}
            handleSelectedPlan={handleSelectedPlan}
            plans={plansResponse}
            isPending={isPending}
            canChangePlan={can('changePackage')}
          />
        )}

        {/* 4. Custom Plan footer */}
        <div className="my-8">
          <div className="max-w-xl mx-auto border-dashed border-2 border-gray-300 p-6 space-y-4 text-center rounded-xl">
            <h5 className="font-bold text-gray-900">Custom Plan</h5>
            <p className="text-sm text-gray-500">
              For plans that we offer if you need more seats, review requests, or
              integrations to your personal CRM, etc. This is available upon
              request.
            </p>
            <a href={`mailto:${ContactUsEmail}`} className="inline-block">
              <Button className="mx-auto">Contact us</Button>
            </a>
          </div>
        </div>
      </motion.div>

      {/* Upgrade Modal */}
      <BaseDialog open={upperModal} onClose={() => handleSelectedPlan()}>
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-brand-lightBlue3 flex items-center p-2 rounded-lg">
            {(() => {
              const PlanIcon = getPlanIcon(selectedPlan?.type || '');
              return (
                <PlanIcon
                  width={24}
                  height={24}
                  className="[&_path]:stroke-brand-baseBlue"
                />
              );
            })()}
          </div>
          <div className="mr-auto my-auto">
            <h5 className="font-bold text-lg leading-tight">
              Upgrade to{' '}
              <span className="capitalize">
                {getPlanDisplayName(selectedPlan?.type || '')}
              </span>
            </h5>
          </div>
          <div>
            <button onClick={() => handleSelectedPlan()} type="button">
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6">
          <div className="mb-4 space-y-4">
            <p className="text-sm text-brand-darkBlue2 capitalize">
              {getPlanDisplayName(selectedPlan?.type || '')}
            </p>
            <p>
              <span className="font-bold text-3xl">${selectedPlan?.price}</span>{' '}
              <span className="text-base-coolGray">/montly</span>
            </p>
            <p className="text-sm text-base-davyGray">
              After payment, all features become immediately available
            </p>
          </div>
          <ul className="[&_li]:border-t [&_li]:py-2 text-sm">
            <li className="font-bold">Everything from lower plan, plus:</li>
            {(() => {
              // Get features that are in the selected plan but not in current plan
              const selectedPlanData = (
                plansResponse?.data as any
              )?.plans?.find((p: any) => p.type === selectedPlan?.type);
              const currentPlanData = (plansResponse?.data as any)?.plans?.find(
                (p: any) => p.type === businessSubscription?.plan?.type
              );

              if (!selectedPlanData || !currentPlanData) return null;

              const selectedFeatures =
                selectedPlanData.features?.map((f: any) => f.name) || [];
              const currentFeatures =
                currentPlanData.features?.map((f: any) => f.name) || [];

              // Find features that are in selected plan but not in current plan
              const newFeatures = selectedFeatures.filter(
                (feature: string) => !currentFeatures.includes(feature)
              );

              return newFeatures.map((featureName: string, index: number) => (
                <li
                  key={index}
                  className="text-base-davyGray flex items-center gap-2"
                >
                  <CheckIcon
                    className="text-brand-baseBlue"
                    width={16}
                    height={16}
                  />
                  <span>{featureName}</span>
                </li>
              ));
            })()}
          </ul>
        </div>
        <div className="px-6 pb-6 flex items-center justify-between">
          <Button kind="danger" onClick={() => handleSelectedPlan()}>
            Cancel
          </Button>
          <Button
            onClick={() => updateSubscription(selectedPlan?.priceId as string)}
          >
            Upgrade
          </Button>
        </div>
      </BaseDialog>

      {/* Downgrade Modal */}
      <BaseDialog open={lowerModal} onClose={() => handleSelectedPlan()}>
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            {(() => {
              const PlanIcon = getPlanIcon(selectedPlan?.type || '');
              return (
                <PlanIcon
                  width={24}
                  height={24}
                  className="[&_path]:stroke-brand-baseBlue"
                />
              );
            })()}
          </div>
          <div className="mr-auto my-auto">
            <h5 className="font-bold text-lg leading-tight">
              Downgrade to{' '}
              <span className="capitalize">
                {getPlanDisplayName(selectedPlan?.type || '')}
              </span>
            </h5>
          </div>
          <div>
            <button type="button" onClick={() => handleSelectedPlan()}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6">
          <div className="mb-4 space-y-4">
            <p className="text-sm text-brand-darkBlue2 capitalize">
              {getPlanDisplayName(selectedPlan?.type || '')}
            </p>
            <p>
              <span className="font-bold text-3xl">${selectedPlan?.price}</span>{' '}
              <span className="text-base-coolGray">/montly</span>
            </p>
            <div className="bg-brand-lightBlue3 p-4  rounded-lg text-sm font-medium flex gap-2">
              <div>
                <BulbIcon
                  className="[&_path]:stroke-brand-baseBlue"
                  width={20}
                  height={20}
                />
              </div>
              <div>
                <p>
                  You are going to switch to a plan below your current one. All
                  the features of the current Professional plan will still be
                  available to you until the date of payment of the new invoice.
                  The new invoice will be based on the new plan.
                </p>
              </div>
            </div>

            <p className="text-sm text-base-davyGray">
              After payment, all features become immediately available
            </p>
          </div>
          <ul className="[&_li]:border-t [&_li]:py-2 text-sm">
            <li className="font-bold">You will lose:</li>
            {(() => {
              // Get features that are in the current plan but not in selected plan
              const selectedPlanData = (
                plansResponse?.data as any
              )?.plans?.find((p: any) => p.type === selectedPlan?.type);
              const currentPlanData = (plansResponse?.data as any)?.plans?.find(
                (p: any) => p.type === businessSubscription?.plan?.type
              );

              if (!selectedPlanData || !currentPlanData) return null;

              const selectedFeatures =
                selectedPlanData.features?.map((f: any) => f.name) || [];
              const currentFeatures =
                currentPlanData.features?.map((f: any) => f.name) || [];

              // Find features that are in current plan but not in selected plan
              const lostFeatures = currentFeatures.filter(
                (feature: string) => !selectedFeatures.includes(feature)
              );

              return lostFeatures.map((featureName: string, index: number) => (
                <li key={index} className="flex items-center gap-2">
                  <CheckIcon
                    className="text-brand-baseBlue"
                    width={16}
                    height={16}
                  />
                  <span>{featureName}</span>
                </li>
              ));
            })()}
          </ul>
        </div>
        <div className="px-6 pb-6 flex items-center justify-between">
          <Button kind="secondary" onClick={() => handleSelectedPlan()}>
            Cancel
          </Button>
          <Button
            onClick={() => updateSubscription(selectedPlan?.priceId as string)}
          >
            Downgrade
          </Button>
        </div>
      </BaseDialog>
    </>
  );
};
